import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { bibleService } from '../../../services/bibleService';

/**
 * VerseRangeSelector
 * Shown before starting memorization when a verse is passed from the bible reader.
 * Lets users select a range of verses (e.g. John 3:16-18 or John 3:16,17,19).
 */
const VerseRangeSelector = ({ baseVerse, onConfirm, onSkip }) => {
  const [chapterVerses, setChapterVerses] = useState([]);
  const [selectedVerseNumbers, setSelectedVerseNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const book = baseVerse?.book;
  const chapter = baseVerse?.chapter;
  const version = baseVerse?.version || 'KJV';
  const baseVerseNumber = Number(baseVerse?.verse);
  const baseVerseEnd = Number(baseVerse?.verseEnd) || baseVerseNumber;

  // Load all verses in the chapter
  useEffect(() => {
    if (!book || !chapter) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: err } = await bibleService?.getChapterVerses(book, chapter, version);
        if (err) throw new Error(err.message || 'Failed to load chapter verses');
        setChapterVerses(data || []);
        // Pre-select all verses in the range (or just the base verse for single verses)
        const preSelected = [];
        for (let v = baseVerseNumber; v <= baseVerseEnd; v++) {
          if (!isNaN(v)) preSelected?.push(v);
        }
        setSelectedVerseNumbers(preSelected?.length > 0 ? preSelected : [baseVerseNumber]);
      } catch (e) {
        setError(e?.message);
        const preSelected = [];
        for (let v = baseVerseNumber; v <= baseVerseEnd; v++) {
          if (!isNaN(v)) preSelected?.push(v);
        }
        setSelectedVerseNumbers(preSelected?.length > 0 ? preSelected : [baseVerseNumber]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [book, chapter, version, baseVerseNumber, baseVerseEnd]);

  // Extract verse number from verse object — API returns verse_number
  const getVerseNum = (v) => {
    const n = v?.verse_number ?? v?.number ?? v?.verse;
    return Number(n);
  };

  const toggleVerse = (verseNum) => {
    const num = Number(verseNum);
    setSelectedVerseNumbers(prev => {
      const alreadySelected = prev?.includes(num);
      if (alreadySelected) {
        // Don't allow deselecting if it's the only one selected
        if (prev?.length === 1) return prev;
        return prev?.filter(v => v !== num);
      }
      return [...prev, num]?.sort((a, b) => a - b);
    });
  };

  const buildReference = (nums) => {
    if (!nums || nums?.length === 0) return '';
    const sorted = [...nums]?.sort((a, b) => a - b);

    let isContiguous = true;
    for (let i = 1; i < sorted?.length; i++) {
      if (sorted?.[i] !== sorted?.[i - 1] + 1) { isContiguous = false; break; }
    }

    if (sorted?.length === 1) return `${book} ${chapter}:${sorted?.[0]}`;
    if (isContiguous) return `${book} ${chapter}:${sorted?.[0]}-${sorted?.[sorted?.length - 1]}`;
    return `${book} ${chapter}:${sorted?.join(', ')}`;
  };

  const handleConfirm = () => {
    const sorted = [...selectedVerseNumbers]?.sort((a, b) => a - b);
    const selectedVerseObjects = sorted?.map(num => {
      const found = chapterVerses?.find(v => getVerseNum(v) === num);
      return found || { verse_number: num, text: '' };
    });

    const combinedText = selectedVerseObjects
      ?.map(v => (v?.text || '')?.replace(/\{(?:\()?[HG]\d+(?:\))?\}/g, '')?.trim())
      ?.filter(Boolean)
      ?.join(' ');

    const reference = buildReference(sorted);

    onConfirm?.({
      verses: selectedVerseObjects,
      reference,
      combinedText,
      book,
      chapter,
      verseStart: sorted?.[0],
      verseEnd: sorted?.[sorted?.length - 1],
      version,
    });
  };

  const previewReference = buildReference(selectedVerseNumbers);

  return (
    <div className="bg-card rounded-xl border border-border shadow-warm p-6 max-w-lg mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
          <Icon name="BookOpen" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h2 className="text-lg font-heading font-semibold text-foreground">
            Select Verse Range
          </h2>
          <p className="text-sm text-muted-foreground">
            Choose which verses to memorize from {book} {chapter}
          </p>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-8 gap-3">
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-muted-foreground">Loading chapter verses…</span>
        </div>
      )}

      {/* Error fallback */}
      {error && !loading && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
          <p className="text-sm text-destructive">{error}</p>
          <p className="text-xs text-muted-foreground mt-1">
            You can still proceed with the single verse below.
          </p>
        </div>
      )}

      {/* Verse list */}
      {!loading && chapterVerses?.length > 0 && (
        <div className="mb-5">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
            {book} Chapter {chapter} — tap verses to include
          </p>
          <div className="max-h-64 overflow-y-auto rounded-lg border border-border divide-y divide-border">
            {chapterVerses?.map((v) => {
              const num = getVerseNum(v);
              if (!num || num <= 0) return null;
              const isSelected = selectedVerseNumbers?.includes(num);
              const isBase = num === baseVerseNumber;
              return (
                <button
                  key={num}
                  onClick={() => toggleVerse(num)}
                  className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-all duration-150
                    focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ring
                    active:scale-[0.99]
                    ${isSelected
                      ? 'bg-primary/10 hover:bg-primary/15' :'bg-card hover:bg-muted/40'
                    }`}
                >
                  {/* Checkbox */}
                  <div className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-150 ${
                    isSelected
                      ? 'bg-primary border-primary scale-105' :'border-border bg-transparent hover:border-primary/50'
                  }`}>
                    {isSelected && (
                      <Icon name="Check" size={12} color="white" />
                    )}
                  </div>
                  {/* Verse number + text */}
                  <div className="flex-1 min-w-0">
                    <span className={`text-xs font-bold mr-2 transition-colors ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}>
                      v{num}
                      {isBase && (
                        <span className="ml-1 text-xs font-normal text-primary/70">(selected)</span>
                      )}
                    </span>
                    <span className={`text-sm leading-relaxed transition-colors ${isSelected ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {(v?.text || '')?.replace(/\{(?:\()?[HG]\d+(?:\))?\}/g, '')?.trim()}
                    </span>
                  </div>
                </button>
              );
            })?.filter(Boolean)}
          </div>
        </div>
      )}

      {/* Preview reference */}
      {selectedVerseNumbers?.length > 0 && (
        <div className="mb-5 p-3 bg-muted/40 rounded-lg border border-border flex items-center gap-2">
          <Icon name="BookMarked" size={16} color="var(--color-primary)" className="flex-shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Will memorize:</p>
            <p className="text-sm font-semibold text-foreground">{previewReference}</p>
            {selectedVerseNumbers?.length > 1 && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {selectedVerseNumbers?.length} verses combined
              </p>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          className="flex-1"
          onClick={onSkip}
        >
          Use Single Verse
        </Button>
        <Button
          variant="default"
          className="flex-1"
          onClick={handleConfirm}
          disabled={selectedVerseNumbers?.length === 0 || loading}
          iconName="Play"
          iconPosition="left"
        >
          Start Practice
        </Button>
      </div>
    </div>
  );
};

export default VerseRangeSelector;
